import { PackageItem } from "@shared/components/landing-pages/ux-audit/package-item";
import { Packages, SERVICES } from "@shared/components/landing-pages/ux-audit/services";
import { Translation } from "@shared/types/translation";
import { useLocale } from "@translations/locale-context";
import { Icon } from "@ui/components/icon/icon";
import { Tooltip } from "@uxf/ui/tooltip";
import React from "react";

const PRICES: {
    medium: Translation;
    platinum: Translation;
}[] = [
    {
        medium: {
            cs: "90\u00a0000\u00a0Kč",
            en: "3\u00a0600\u00a0EUR",
        },
        platinum: {
            cs: "170\u00a0000\u00a0Kč",
            en: "6\u00a0800\u00a0EUR",
        },
    },
];

const czk = "Cena bez DPH";
const eur = "PRICE (excluding vat)";

// Mobile

interface Props {
    package: Packages;
    price: string;
}

const ItemMobile = (props: Props) => {
    const locale: "cs" | "en" = useLocale();

    return (
        <div className="px-5 py-6 odd:bg-black/5">
            <p className="py-4 text-center text-body font-bold uppercase">{props.package}</p>
            <div>
                <ul>
                    {SERVICES.map(({ packages, cs, en }, index) => (
                        <li key={index}>
                            <div className="mb-4 flex items-center space-x-2 pt-2 text-small1">
                                <Icon
                                    name={packages.includes(props.package) ? "checkSimple" : "cross"}
                                    size={packages.includes(props.package) ? 22 : 18}
                                    className={packages.includes(props.package) ? "text-uxfGreen" : "text-uxfWhite75"}
                                />
                                <Tooltip
                                    content={
                                        <div className="px-5">
                                            <ul className="list-disc">
                                                <li className="pb-2">{locale === "en" ? en.point1 : cs.point1}</li>
                                                <li>{locale === "en" ? en.point2 : cs.point2}</li>
                                            </ul>
                                        </div>
                                    }
                                    placement="bottom"
                                    className="bg-uxfMuted75 px-4 py-5 text-white sm:text-small2"
                                >
                                    <div className="flex space-x-2">
                                        <p
                                            className={`pl-2 ${
                                                packages.includes(props.package)
                                                    ? "text-uxfVioletDark"
                                                    : "text-uxfWhite75"
                                            } ${
                                                packages.includes(props.package)
                                                    ? "underline underline-offset-4"
                                                    : "line-through"
                                            }`}
                                        >
                                            {locale === "en" ? en.title : cs.title}
                                        </p>
                                        <Icon
                                            name="infoInverted"
                                            size={16}
                                            className={`mt-1 ${
                                                packages.includes(props.package) ? "text-uxfMuted75" : "text-uxfWhite75"
                                            }`}
                                        />
                                    </div>
                                </Tooltip>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="flex justify-between py-6 text-small1 font-bold text-uxfVioletDark sm:text-body">
                <p>{locale === "en" ? eur : czk}</p>
                <p>{props.price}</p>
            </div>
        </div>
    );
};

const ContentMobile = () => {
    const locale: "cs" | "en" = useLocale();
    return (
        <div className="lg:hidden">
            {PRICES.map(({ medium, platinum }, index) => (
                <div key={index}>
                    <ItemMobile package="Medium" price={medium[locale]} />
                    <ItemMobile package="Platinum" price={platinum[locale]} />
                </div>
            ))}
        </div>
    );
};

//Desktop

interface StatusProps {
    included?: boolean;
}

const ItemStatus = (props: StatusProps) => {
    return (
        <div className="flex h-[80px] w-[120px] items-center justify-center">
            <Icon
                className={props.included ? "text-uxfGreen" : "text-uxfWhite75"}
                name={props.included ? "checkInverted" : "cross"}
                size={props.included ? 24 : 16}
            />
        </div>
    );
};

const ServiceItem = () => {
    const locale: "cs" | "en" = useLocale();

    return (
        <div className="max-lg:hidden">
            {SERVICES.map(({ packages, cs, en }, index) => (
                <div key={index} className="grid grid-cols-[5.5fr_3fr] even:bg-black/5">
                    <div className="max-w-[550px] px-6 pt-6 text-body xl:max-w-[690px]">
                        <PackageItem
                            title={locale === "en" ? en.title : cs.title}
                            point2={locale === "en" ? en.point2 : cs.point2}
                            point1={locale === "en" ? en.point1 : cs.point1}
                            isDefaultOpen={index === 0}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-24">
                        <ItemStatus included={packages.includes("Medium")} />
                        <ItemStatus included={packages.includes("Platinum")} />
                    </div>
                </div>
            ))}
        </div>
    );
};

const Content = () => {
    const locale: "cs" | "en" = useLocale();
    return (
        <div className="container max-lg:hidden">
            <div className="grid grid-cols-[5.5fr_3fr] text-body font-bold uppercase text-uxfVioletDark lg:text-medium">
                <div className="col-start-2 grid grid-cols-2 gap-24">
                    <p className="w-[120px] text-center">Medium</p>
                    <p className="w-[120px] text-center">Platinum</p>
                </div>
            </div>
            <ServiceItem />
            <div className="py-12 pl-6 text-small1 font-bold text-uxfVioletDark xl:py-12 xl:text-body">
                {PRICES.map(({ medium, platinum }, index) => (
                    <div key={index} className="grid grid-cols-[5.5fr_3fr]">
                        <p>{locale === "en" ? eur : czk}</p>
                        <div className="grid grid-cols-2 gap-24">
                            <p className="w-[120px] text-center">{medium[locale]}</p>
                            <p className="w-[120px] text-center">{platinum[locale]}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

//Component
export const AuditPackages = () => {
    return (
        <div className="container pt-6 lg:py-14">
            <Content />
            <ContentMobile />
        </div>
    );
};
